import { useState } from 'react';
import { AlertTriangle, Loader2, CheckCircle, Zap } from 'lucide-react';
import { clsx } from 'clsx';
import { useForceEncompassStatus } from '../../hooks/useLosProxy';

const STATUSES = [
  { value: 'Processing', label: 'Processing', color: 'text-amber-600', description: 'Mark as processing' },
  { value: 'Completed', label: 'Completed', color: 'text-green-600', description: 'Mark as completed' },
  { value: 'Failed', label: 'Failed', color: 'text-red-600', description: 'Mark as failed' },
  { value: 'Canceled', label: 'Canceled', color: 'text-gray-600', description: 'Mark as canceled' },
];

interface Props {
  transactionId: string;
}

export default function EncompassForceStatus({ transactionId }: Props) {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [reason, setReason] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const forceStatus = useForceEncompassStatus(transactionId);

  if (!transactionId) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">No transaction ID available</p>
      </div>
    );
  }

  const handleSubmit = () => {
    if (!selectedStatus) return;
    forceStatus.mutate(
      { status: selectedStatus, reason: reason || undefined },
      {
        onSuccess: () => {
          setShowConfirm(false);
          setSelectedStatus('');
          setReason('');
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-amber-400" />
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Force Transaction Status</h4>
      </div>

      <div className="p-3 rounded bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-500/20">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Caution</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              Forcing a status update will directly modify the Encompass transaction response.
              This should only be used to unblock stuck orders.
            </p>
          </div>
        </div>
      </div>

      {forceStatus.isSuccess && (
        <div className="p-3 rounded bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-500/20">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <p className="text-xs text-green-700 dark:text-green-300">Status updated successfully</p>
          </div>
        </div>
      )}

      {forceStatus.isError && (
        <div className="p-3 rounded bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-500/20">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <p className="text-xs text-red-700 dark:text-red-300">
              Failed: {(forceStatus.error as Error).message}
            </p>
          </div>
        </div>
      )}

      {/* Status selection */}
      <div>
        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-2">Target Status</label>
        <div className="grid grid-cols-2 gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => setSelectedStatus(s.value)}
              className={clsx(
                'p-2.5 rounded border text-left transition-colors',
                selectedStatus === s.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
              )}
            >
              <p className={clsx('text-xs font-medium', s.color)}>{s.label}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{s.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Reason */}
      <div>
        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Reason (optional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you forcing this status change?"
          className="w-full text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-700 dark:text-gray-300 resize-none h-16"
        />
      </div>

      {/* Submit */}
      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          disabled={!selectedStatus}
          className="btn-primary text-xs gap-1.5 w-full justify-center disabled:opacity-50"
        >
          <Zap className="h-3.5 w-3.5" />
          Force Status Update
        </button>
      ) : (
        <div className="p-3 rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-950/20 space-y-3">
          <p className="text-xs font-medium text-red-700 dark:text-red-300">
            Confirm: Force status to "{selectedStatus}" for transaction {transactionId.slice(0, 12)}...?
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={forceStatus.isPending}
              className="btn-danger text-xs gap-1.5 flex-1 justify-center"
            >
              {forceStatus.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating...</>
              ) : (
                'Confirm'
              )}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="btn-secondary text-xs flex-1 justify-center"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
